import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isNativeAppRuntime } from "../utils/nativeApp";

const AGE_CONSENT_STORAGE_KEY = "huangguo:age-confirmed:v1";

function hasAcceptedAgeGate(): boolean {
  if (typeof window === "undefined") return false;

  try {
    return window.localStorage.getItem(AGE_CONSENT_STORAGE_KEY) === "accepted";
  } catch {
    return false;
  }
}

export default function AgeGate() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(
    () => !isNativeAppRuntime() && !hasAcceptedAgeGate(),
  );

  useEffect(() => {
    if (!isOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAccept = () => {
    try {
      window.localStorage.setItem(AGE_CONSENT_STORAGE_KEY, "accepted");
    } catch {
      // Keep the current visit usable when persistent storage is unavailable.
    }
    setIsOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 px-5 py-8">
      <section
        aria-labelledby="age-gate-title"
        aria-describedby="age-gate-description"
        aria-modal="true"
        role="dialog"
        className="w-full max-w-md rounded-lg border border-orange-500/45 bg-[#171717] p-6 text-center shadow-2xl sm:p-8"
      >
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#F97316] text-xl font-black text-black">
          18+
        </div>

        <div className="mt-5 flex items-center justify-center gap-2 text-[#FB923C]">
          <ShieldCheck aria-hidden="true" className="h-5 w-5" />
          <span className="text-sm font-semibold">{t("cinema_ageGateBadge")}</span>
        </div>

        <h1 id="age-gate-title" className="mt-2 text-2xl font-bold text-white sm:text-3xl">
          {t("cinema_ageGateTitle")}
        </h1>
        <p
          id="age-gate-description"
          className="mt-4 text-sm leading-7 text-neutral-300 sm:text-base"
        >
          {t("cinema_ageGateDescription")}
        </p>
        <p className="mt-3 text-sm font-medium text-orange-300">
          {t("cinema_ageGateUnderage")}
        </p>

        <button
          type="button"
          onClick={handleAccept}
          className="mt-6 min-h-12 w-full rounded-lg bg-[#F97316] px-4 py-3 text-base font-bold text-black transition-colors hover:bg-[#FB923C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FB923C] focus-visible:ring-offset-2 focus-visible:ring-offset-[#171717]"
        >
          {t("cinema_ageGateConfirm")}
        </button>
      </section>
    </div>
  );
}
